<template>
  <div class="wrapper" ref="wrapperRef">
    <div class="card" ref="topRef">
      <slot name="formTop"></slot>
      <el-form
        :model="form"
        inline
        ref="formRef"
        class="form"
        :label-width="labelWidth"
      >
        <div class="form-left" ref="leftRef">
          <el-form-item
            v-for="item in realFormColumns"
            :key="item.prop"
            :label="item.label"
            :label-width="item.labelWidth ? item.labelWidth : labelWidth"
          >
            <el-select
              v-if="item.type === 'select'"
              v-model="form[item.prop]"
              clearable
              placeholder="请选择"
            >
              <el-option
                v-for="option in item.source"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              ></el-option>
            </el-select>
            <el-date-picker
              v-else-if="item.type === 'date'"
              v-model="form[item.prop]"
              value-format="YYYY-MM-DD"
              :placeholder="item.placeholder"
            >
            </el-date-picker>
            <el-date-picker
              v-else-if="item.type === 'time'"
              v-model="form[item.prop]"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              :placeholder="item.placeholder"
            >
            </el-date-picker>
            <el-cascader
              v-else-if="item.type === 'cascader'"
              v-model="form[item.prop]"
              :options="item.source"
              clearable
              :props="{
                expandTrigger: 'hover',
                label: 'name',
                value: 'id',
                emitPath: false,
                checkStrictly: true,
              }"
            ></el-cascader>

            <div v-else-if="item.type === 'slot'">
              <slot :name="item.slotName"> </slot>
            </div>

            <el-input
              v-else
              v-model="form[item.prop]"
              placeholder="请输入"
            ></el-input>
          </el-form-item>
        </div>
        <div class="form-right">
          <el-button type="primary" @click="fetchData">查询</el-button>
          <el-button @click="reset">重置</el-button>
          <el-link type="primary" v-if="showFoldBtn" @click="foldFun"
            >{{ fold ? "展开" : "收起" }}<el-icon
              ><ArrowUp v-if="!fold" /><ArrowDown v-if="fold" /></el-icon
          ></el-link>
        </div>
      </el-form>
    </div>

    <div class="card table-wrapper" ref="btmRef">
      <div class="slotContent" ref="slotContentRef">
        <slot name="formRight"></slot>
      </div>
      <el-table
        ref="tableRef"
        :data="tableData"
        :show-overflow-tooltip="showTooltip"
        border
        :height="tableHeight"
        @selection-change="selectChange"
      >
        <el-table-column
          v-if="showIndex"
          type="index"
          width="55"
          label="序号"
        ></el-table-column>
        <el-table-column
          v-if="showSelection"
          type="selection"
          width="55"
        ></el-table-column>
        <el-table-column
          v-for="item in columns"
          :key="item.prop"
          :label="item.label"
          :width="item.width"
        >
          <template #default="{ row }">
            <span
              :class="['cell-content', getClass(item, row)]"
              :title="getContent(item, row)"
              >{{ getContent(item, row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          fixed="right"
          v-if="showOperation"
          :width="operationWidth"
        >
          <template #default="scope">
            <slot name="operation" :currentProps="scope.row"> </slot>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="form.current"
        v-model:page-size="form.size"
        :page-sizes="[10, 20, 30, 50]"
        :layout="pageLayout"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from "vue";

const emit = defineEmits(["selectChange", "reset"]);

const props = defineProps({
  formColumns: Array,
  showIndex: {
    type: Boolean,
    default: true,
  },
  columns: Array,
  showOperation: {
    type: Boolean,
    default: true,
  },
  operationWidth: Number,
  showSelection: {
    type: Boolean,
    default: false,
  },
  size: {
    type: Number,
    default: 10,
  },
  layout: {
    type: String,
    default: "total, sizes, prev, pager, next, jumper",
  },
  otherForm: {
    type: Object,
    default: () => ({}),
  },
  fetchFun: Function,
  showTooltip: {
    type: Boolean,
    default: false,
  },
});

const showFoldBtn = ref(false);
const fold = ref(false); // 是否折叠
const formItemLen = ref(0); // 一行显示多少个form-item
const topRef = ref(null);
const formRef = ref(null);
const leftRef = ref(null);
const total = ref(0);
const tableData = ref([]);
const tableRef = ref(null);
const wrapperRef = ref(null);
const selection = ref([]); // 当前列表页选项
const tableHeight = ref(null);

const realFormColumns = computed(() => {
  return fold.value
    ? props.formColumns.slice(0, formItemLen.value)
    : props.formColumns;
});

const labelWidth = computed(() => {
  const maxLabelLength = Math.max(
    ...props.formColumns.map((item) => item.label.length)
  );
  return maxLabelLength * 16 + 5;
});

const pageLayout = computed(() => {
  return props.layout;
});
const form = ref({
  current: 1,
  size: props.size,
});

function selectChange(val) {
  selection.value = val;
  emit("selectChange", val);
}
function clearSelection() {
  tableRef.value.clearSelection();
}

async function fetchData() {
  if (!props.fetchFun) return;
  const params = Object.assign({}, props.otherForm, form.value);
  const { data } = await props.fetchFun(params);
  tableData.value = data?.records;
  total.value = data?.total;
}

function reset() {
  emit("reset");
  const { size } = form.value;
  form.value = {
    current: 1,
    size,
  };
  fetchData();
}

function handleCurrentChange(val) {
  form.value.current = val;
  fetchData();
}

function handleSizeChange(val) {
  form.value.size = val;
  form.value.current = 1;
  fetchData();
}
function foldFun() {
  fold.value = !fold.value;
  nextTick(() => {
    calHeight();
  });
}

const slotContentRef = ref(null);
function calHeight() {
  const parentH = wrapperRef.value.clientHeight;
  let height = null;
  height =
    parentH -
    slotContentRef.value.clientHeight -
    topRef.value.clientHeight -
    80;
  tableHeight.value = height;
}

function getContent(item, row) {
  if (item.formatFun) return item.formatFun(item.prop, row);
  return row[item.prop];
}
function getClass(item, row) {
  if (item.classFun) return item.classFun(item.prop, row);
  return row[item.prop];
}

onMounted(() => {
  const formH = formRef.value.$el.clientHeight;
  showFoldBtn.value = formH / 50 > 1;
  if (showFoldBtn.value) fold.value = true;
  const leftW = leftRef.value.clientWidth;
  formItemLen.value = Math.floor(leftW / (202 + labelWidth.value));
  fetchData();
  nextTick(() => {
    calHeight();
  });
});

defineExpose({ form, fetchData, selection, clearSelection });
</script>

<style lang="scss" scoped>
.wrapper {
  height: 100%;
  .card {
    background-color: #fff;
    border-radius: 10px;
    padding: 15px 20px 0 20px;
    margin-bottom: 10px;
  }
  .table-wrapper {
    padding-bottom: 10px;
  }
}
.form {
  display: flex;
  .form-left {
    flex: 1;
  }

  .form-right {
    min-width: 132px;
    max-width: 184px;
  }
}
::v-deep .el-form-item__content {
  width: 170px;
}
.table {
  border-radius: 10px;
  background: #fff;
  padding: 10px;
  width: calc(100% - 20px);
}
.slotContent {
  padding-bottom: 10px;
}
.cell-content {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  line-height: 1.5;
}
</style>
